import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DialogFlowDetails from './DialogFlowDetails';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'react-hot-toast';
import AppLoadingState from './common/AppLoadingState';
import { AppPageShell, PageHeader } from './common/ui';
import {
  HiOutlineMagnifyingGlass,
  HiOutlineClipboardDocument,
  HiOutlineCheck,
  HiOutlineEllipsisVertical,
  HiOutlinePlus,
  HiOutlineCubeTransparent,
} from 'react-icons/hi2';

const DialogFlowEditor = () => {
  const [dialogFlows, setdialogFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const navigate = useNavigate();
  const { dialogFlowId } = useParams();
  const [openMenuId, setOpenMenuId] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedDialogFlowId, setSelectedDialogFlowId] = useState(null);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [newDialogFlowName, setNewDialogFlowName] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchdialogFlows();
  }, []);

  useEffect(() => {
    if (openMenuId == null) return;
    const onPointerDown = (e) => {
      if (!e.target.closest('[data-df-menu-root]')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [openMenuId]);

  const fetchdialogFlows = async () => {
    try {
      setLoading(true);
      const response = await api.getdialogFlows();

      const dialogFlowsArray = Array.isArray(response) ? response : [response];

      const dialogFlowsWithDetails = await Promise.all(
        dialogFlowsArray.map(async (dialogFlow) => {
          const detailsResponse = await api.getdialogFlow(dialogFlow.id);
          return {
            id: dialogFlow.id || Math.random().toString(36).substr(2, 9),
            name: detailsResponse.name || 'Unnamed dialogFlow',
            description: detailsResponse.description || 'No description available',
            nodesCount: (detailsResponse.nodes || []).length,
            edgesCount: (detailsResponse.edges || []).length,
            status: 'active',
          };
        })
      );

      setdialogFlows(dialogFlowsWithDetails);
      setError(null);
    } catch (err) {
      console.error('Error fetching dialogFlows:', err);
      setError('Failed to load dialogFlows');
    } finally {
      setLoading(false);
    }
  };

  const handleOpendialogFlow = (id) => {
    navigate(`/app/dialog-flows/${id}`);
  };

  const handleClosePath = () => {
    navigate('/app/dialog-flows');
  };

  const handleCreateDialogFlow = async () => {
    try {
      const response = await api.createDialogFlow(formData);
      setIsModalOpen(false);
      setFormData({ name: '', description: '' });
      navigate(`/app/dialog-flows/${response.dialogFlowId}`);
    } catch (error) {
      console.error('Error creating dialog flow:', error);
      toast.error('Could not create dialog flow');
    }
  };

  const handleDeleteDialogFlow = async (id) => {
    try {
      await api.deleteDialogFlow(id);
      fetchdialogFlows();
    } catch (error) {
      console.error('Error deleting dialog flow:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.details?.message ||
        'Failed to delete dialog flow';
      toast.error(errorMessage);
      if (error.response?.status === 404 || errorMessage.includes('not found')) {
        fetchdialogFlows();
      }
    }
  };

  const handleRenameDialogFlow = async () => {
    if (selectedDialogFlowId && newDialogFlowName) {
      try {
        await api.renameDialogFlow(selectedDialogFlowId, newDialogFlowName);
        fetchdialogFlows();
        setIsRenameModalOpen(false);
        setSelectedDialogFlowId(null);
      } catch (error) {
        console.error('Error renaming dialog flow:', error);
        toast.error('Could not rename dialog flow');
      }
    }
  };

  const toggleMenu = useCallback((id, e) => {
    e?.stopPropagation();
    setOpenMenuId((prev) => (prev === id ? null : id));
  }, []);

  const openConfirmModal = (id) => {
    setSelectedDialogFlowId(id);
    setIsConfirmModalOpen(true);
    setOpenMenuId(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedDialogFlowId) {
      await handleDeleteDialogFlow(selectedDialogFlowId);
      setIsConfirmModalOpen(false);
      setSelectedDialogFlowId(null);
    }
  };

  const openRenameModal = (id, currentName) => {
    setSelectedDialogFlowId(id);
    setNewDialogFlowName(currentName);
    setIsRenameModalOpen(true);
    setOpenMenuId(null);
  };

  const handleCopyId = (id) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const shortenId = (id) => {
    if (!id || id.length < 14) return id;
    return id.substring(0, 8) + '…' + id.substring(id.length - 4);
  };

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredFlows = useMemo(() => {
    if (!normalizedQuery) return dialogFlows;
    return dialogFlows.filter((flow) => {
      const hay = `${flow.name} ${flow.description} ${flow.id}`.toLowerCase();
      return hay.includes(normalizedQuery);
    });
  }, [dialogFlows, normalizedQuery]);

  if (dialogFlowId) {
    return (
      <DialogFlowDetails dialogFlowId={dialogFlowId} onClose={handleClosePath} />
    );
  }

  const modalBackdrop =
    'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-[3px]';
  const modalPanel =
    'w-full max-w-md rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] p-6 shadow-[var(--app-popover-shadow)]';
  const fieldClass =
    'w-full rounded-xl border border-[var(--app-field-border)] bg-[var(--app-field-bg)] px-3 py-2.5 text-sm text-[var(--app-ink)] placeholder:text-[var(--app-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--app-accent-ring)]';
  const btnGhost =
    'rounded-xl px-4 py-2.5 text-sm font-semibold text-[var(--app-muted)] transition hover:bg-[var(--app-accent-soft)] hover:text-[var(--app-ink)]';
  const btnPrimary =
    'inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--app-accent)] px-5 py-2.5 text-sm font-semibold text-slate-950 shadow-md transition hover:bg-[var(--app-accent-hover)] hover:shadow-lg dark:text-slate-950';
  const btnDanger =
    'rounded-xl bg-[var(--app-danger)] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[var(--app-danger-hover)]';

  return (
    <AppPageShell maxWidthClass="max-w-6xl">
      <PageHeader
        eyebrow="Conversation design"
        title="Dialog Flows"
        description="Blueprints for how your agents listen, branch, and respond. Open a flow to edit the graph, or spin up a new one from scratch."
        actions={
          <button type="button" className={btnPrimary} onClick={() => setIsModalOpen(true)}>
            <HiOutlinePlus className="h-5 w-5 shrink-0" aria-hidden />
            Create Dialog Flow
          </button>
        }
      />

      {isModalOpen && (
        <div className={modalBackdrop} role="presentation">
          <div className={modalPanel} role="dialog" aria-labelledby="df-create-title">
            <h3 id="df-create-title" className="text-lg font-semibold text-[var(--app-ink)]">
              Create Dialog Flow
            </h3>
            <p className="mt-1 text-sm text-[var(--app-muted)]">
              Name it clearly — you will see this in campaigns and routing.
            </p>
            <input
              type="text"
              placeholder="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`${fieldClass} mt-5`}
            />
            <textarea
              placeholder="Description (optional)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className={`${fieldClass} mt-3 resize-y`}
            />
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className={btnGhost} onClick={() => setIsModalOpen(false)}>
                Cancel
              </button>
              <button type="button" className={btnPrimary} onClick={handleCreateDialogFlow}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      <section aria-labelledby="df-list-heading">
        <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 id="df-list-heading" className="text-base font-semibold text-[var(--app-ink)]">
            Your flows
            {!loading && !error && dialogFlows.length > 0 ? (
              <span className="ml-2 font-normal text-[var(--app-muted)]">
                ({filteredFlows.length}
                {normalizedQuery ? ` of ${dialogFlows.length}` : ''})
              </span>
            ) : null}
          </h2>
          {!loading && !error && dialogFlows.length > 0 ? (
            <div className="relative w-full sm:max-w-xs">
              <HiOutlineMagnifyingGlass
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--app-muted)]"
                aria-hidden
              />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, ID, or description…"
                aria-label="Search dialog flows"
                className={`${fieldClass} pl-10`}
              />
            </div>
          ) : null}
        </div>

        {loading ? (
          <AppLoadingState
            layout="compact"
            label="Loading dialog flows…"
            description="Fetching graphs and metadata."
          />
        ) : error ? (
          <div
            className="rounded-2xl border border-[var(--app-alert-error-border)] bg-[var(--app-alert-error-bg)] px-5 py-4 text-[var(--app-alert-error-fg)]"
            role="alert"
          >
            {error}
          </div>
        ) : dialogFlows.length === 0 ? (
          <div className="relative overflow-hidden rounded-3xl border border-dashed border-[var(--app-empty-border)] bg-[var(--app-empty-bg)] px-8 py-16 text-center shadow-inner">
            <div
              className="pointer-events-none absolute left-1/2 top-8 h-32 w-32 -translate-x-1/2 rounded-full opacity-80 blur-2xl"
              style={{ background: 'var(--app-accent-soft)' }}
              aria-hidden
            />
            <HiOutlineCubeTransparent
              className="relative mx-auto h-14 w-14 text-[var(--app-accent)]"
              aria-hidden
            />
            <p className="relative mt-6 text-lg font-semibold text-[var(--app-ink)]">
              No dialog flows yet
            </p>
            <p className="relative mx-auto mt-2 max-w-md text-sm text-[var(--app-muted)]">
              Create your first flow to map intents, branches, and handoffs. You can refine the
              graph anytime.
            </p>
            <button
              type="button"
              className={`${btnPrimary} relative mt-8`}
              onClick={() => setIsModalOpen(true)}
            >
              <HiOutlinePlus className="h-5 w-5" aria-hidden />
              Create Dialog Flow
            </button>
          </div>
        ) : filteredFlows.length === 0 ? (
          <div className="rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] px-6 py-12 text-center">
            <p className="font-medium text-[var(--app-ink)]">No matches for “{searchQuery.trim()}”</p>
            <p className="mt-2 text-sm text-[var(--app-muted)]">Try another keyword or clear the search.</p>
            <button
              type="button"
              className={`${btnGhost} mt-4 font-semibold text-[var(--app-accent)]`}
              onClick={() => setSearchQuery('')}
            >
              Clear search
            </button>
          </div>
        ) : (
          <ul className="grid list-none gap-4 sm:grid-cols-1 lg:grid-cols-2">
            {filteredFlows.map((dialogFlow, index) => (
              <li
                key={dialogFlow.id}
                className="app-stagger-card group relative rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] p-5 shadow-[var(--app-panel-shadow)] transition duration-300 hover:-translate-y-0.5 hover:border-[var(--app-accent-ring)] hover:shadow-lg"
                style={{ animationDelay: `${Math.min(index * 48, 520)}ms` }}
              >
                <div
                  className="pointer-events-none absolute left-0 top-4 h-12 w-1 rounded-r-full bg-[var(--app-accent)] opacity-0 transition duration-300 group-hover:opacity-100"
                  aria-hidden
                />
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-base font-semibold text-[var(--app-ink)]">
                      {dialogFlow.name}
                    </h3>
                    <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-[var(--app-muted)]">
                      {dialogFlow.description}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[var(--app-muted)]">
                      <span className="app-page-mono inline-flex items-center gap-1.5 rounded-lg bg-[var(--app-readonly-bg)] px-2 py-1 font-medium text-[var(--app-ink)]">
                        {shortenId(dialogFlow.id)}
                        <button
                          type="button"
                          onClick={() => handleCopyId(dialogFlow.id)}
                          className="rounded-md p-0.5 text-[var(--app-accent)] transition hover:bg-[var(--app-accent-soft)]"
                          title="Copy full ID"
                        >
                          {copiedId === dialogFlow.id ? (
                            <HiOutlineCheck className="h-4 w-4 text-emerald-500" aria-hidden />
                          ) : (
                            <HiOutlineClipboardDocument className="h-4 w-4" aria-hidden />
                          )}
                          <span className="sr-only">Copy ID</span>
                        </button>
                      </span>
                      <span>
                        {dialogFlow.nodesCount} nodes · {dialogFlow.edgesCount} edges
                      </span>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 self-end sm:self-start sm:flex-col sm:items-stretch">
                    <button
                      type="button"
                      onClick={() => handleOpendialogFlow(dialogFlow.id)}
                      className={btnPrimary}
                    >
                      Open
                    </button>
                    <div className="relative flex justify-end" data-df-menu-root>
                      <button
                        type="button"
                        className="rounded-xl p-2 text-[var(--app-muted)] transition hover:bg-[var(--app-nav-hover-bg)] hover:text-[var(--app-ink)]"
                        aria-expanded={openMenuId === dialogFlow.id}
                        aria-haspopup="true"
                        aria-label={`More actions for ${dialogFlow.name}`}
                        onClick={(e) => toggleMenu(dialogFlow.id, e)}
                      >
                        <HiOutlineEllipsisVertical className="h-5 w-5" aria-hidden />
                      </button>
                      {openMenuId === dialogFlow.id && (
                        <div
                          className="absolute right-0 top-full z-50 mt-1 min-w-[10rem] overflow-hidden rounded-xl border border-[var(--app-border)] bg-[var(--app-surface-strong)] py-1 shadow-[var(--app-popover-shadow)]"
                          role="menu"
                        >
                          <button
                            type="button"
                            role="menuitem"
                            className="block w-full px-4 py-2.5 text-left text-sm text-[var(--app-ink)] transition hover:bg-[var(--app-nav-hover-bg)]"
                            onClick={() => openRenameModal(dialogFlow.id, dialogFlow.name)}
                          >
                            Rename
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="block w-full px-4 py-2.5 text-left text-sm text-[var(--app-danger)] transition hover:bg-[var(--app-danger-soft)]"
                            onClick={() => openConfirmModal(dialogFlow.id)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {isConfirmModalOpen && (
        <div className={modalBackdrop}>
          <div className={modalPanel} role="alertdialog" aria-labelledby="df-delete-title">
            <h3 id="df-delete-title" className="text-lg font-semibold text-[var(--app-ink)]">
              Delete dialog flow?
            </h3>
            <p className="mt-2 text-sm text-[var(--app-muted)]">
              This cannot be undone. Any campaigns using this flow may need to be updated.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className={btnGhost}
                onClick={() => setIsConfirmModalOpen(false)}
              >
                Cancel
              </button>
              <button type="button" className={btnDanger} onClick={handleConfirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {isRenameModalOpen && (
        <div className={modalBackdrop}>
          <div className={modalPanel} role="dialog" aria-labelledby="df-rename-title">
            <h3 id="df-rename-title" className="text-lg font-semibold text-[var(--app-ink)]">
              Rename Dialog Flow
            </h3>
            <input
              type="text"
              placeholder="New name"
              value={newDialogFlowName}
              onChange={(e) => setNewDialogFlowName(e.target.value)}
              className={`${fieldClass} mt-4`}
            />
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className={btnGhost} onClick={() => setIsRenameModalOpen(false)}>
                Cancel
              </button>
              <button type="button" className={btnPrimary} onClick={handleRenameDialogFlow}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </AppPageShell>
  );
};

export default DialogFlowEditor;
